import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScrolled, useActiveSection } from '../../hooks/use-navigation';
import NavLinks from './nav/NavLinks';
import MobileMenu from './nav/MobileMenu';
import OMCMarkSquare from '../branding/OMCMarkSquare';

const NAV_GROUPS = [
  {
    key: 'who',
    label: 'Who We Are',
    items: [
      { type: 'page', label: 'About OMC', path: '/about', testId: 'site-nav-who-about' },
      { type: 'page', label: 'Our Team', path: '/team', testId: 'site-nav-who-team' },
      { type: 'page', label: 'Safety', path: '/safety', testId: 'site-nav-who-safety' },
    ],
  },
  {
    key: 'what',
    label: 'What We Do',
    items: [
      { type: 'scroll', label: 'Where We Work', id: 'markets', testId: 'site-nav-what-markets' },
      { type: 'scroll', label: 'Capabilities', id: 'capabilities', testId: 'site-nav-what-capabilities' },
      { type: 'scroll', label: 'Project Map', id: 'work', testId: 'site-nav-what-map' },
    ],
  },
  { key: 'careers', label: 'Careers', path: '/careers' },
  { key: 'contact', label: 'Contact', path: '/contact' },
];

const SECTION_IDS = ['top', 'heritage', 'work', 'markets', 'capabilities'];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isScrolled = useScrolled(24);
  const activeSection = useActiveSection(SECTION_IDS, isHome, 'top');
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollToSection = useCallback(
    (id) => {
      const go = () => {
        // The "Capabilities" section is hidden until a market pillar is chosen.
        // Fire an event so WhatWeDo opens it (default "All") + scrolls, instead
        // of landing on an empty section.
        if (id === 'capabilities') {
          window.dispatchEvent(new CustomEvent('omc:open-capabilities'));
          return;
        }
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      };
      if (!isHome) {
        navigate('/');
        setTimeout(go, 200);
      } else {
        go();
      }
      setMobileOpen(false);
    },
    [isHome, navigate],
  );

  const goToPage = useCallback(
    (path) => {
      navigate(path);
      setMobileOpen(false);
    },
    [navigate],
  );

  return (
    <nav
      data-testid="site-nav"
      aria-label="Primary"
      className={`sticky top-0 z-50 border-b transition-[padding,background-color,border-color] duration-300 ${
        isScrolled
          ? 'bg-[hsl(var(--coal)/0.96)] backdrop-blur-md border-white/8 py-2.5'
          : 'bg-[hsl(var(--coal)/0.85)] backdrop-blur-sm border-white/5 py-3.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-8">
        <button
          onClick={() => scrollToSection('top')}
          className="flex items-center gap-3 group shrink-0"
          data-testid="site-nav-logo"
          aria-label="Ozark Mountain Construction — home"
        >
          <OMCMarkSquare size={84} className="group-hover:opacity-90 transition-opacity" />
          <div className="hidden md:flex flex-col text-left">
            <p className="font-industrial font-bold text-[15px] leading-[1.1] tracking-[0.135em] text-white uppercase whitespace-nowrap group-hover:text-[hsl(var(--water-blue-2))] transition-colors">
              Ozark Mtn. Construction
            </p>
            <p className="font-mono text-[9px] tracking-[0.075em] uppercase mt-1 whitespace-nowrap">
              <span className="text-[#7fb2e3]">Water</span>
              <span className="text-white/55"> | </span>
              <span className="text-[#e0aa6e]">Industrial</span>
              <span className="text-white/55"> | </span>
              <span className="text-[#aacf72]">Energy</span>
            </p>
          </div>
        </button>

        <NavLinks
          groups={NAV_GROUPS}
          isHome={isHome}
          activeSection={activeSection}
          onScrollItemClick={scrollToSection}
          onPageItemClick={goToPage}
        />

        <MobileMenu
          open={mobileOpen}
          onOpenChange={setMobileOpen}
          groups={NAV_GROUPS}
          isHome={isHome}
          activeSection={activeSection}
          onScrollItemClick={scrollToSection}
          onPageItemClick={goToPage}
        />
      </div>
    </nav>
  );
}
